import React, { useEffect, useState } from 'react';
import { X, Check, Send, AlertCircle, User, Phone, Mail, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { Job } from './JobApplicationForm';
interface SupplementFormProps {
  job: Job;
  onClose: () => void;
}
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-1234" data-mp="12ub39kx7fj82">*</span>;
export function SupplementForm({
  job,
  onClose
}: SupplementFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    email: '',
    phone: '',
    workAmount: ''
  });
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const payload = {
      access_key: WEB3FORMS_KEY,
      subject: `Komplettering: ${job.title} – ${formData.name}`,
      from_name: formData.name,
      replyto: formData.email,
      Namn: formData.name,
      Ålder: formData.age,
      'E-post': formData.email,
      Telefon: formData.phone,
      'Önskad tjänst': `${job.emoji} ${job.title} (${job.subtitle})`,
      'Hur mycket vill personen jobba': formData.workAmount,
      Typ: 'Komplettering av tidigare ansökan'
    };
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss direkt.');
      }
    } catch {
      setSubmitError('Kunde inte skicka kompletteringen. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-1235" data-mp="1v7k76n2ix78l">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-1236" data-mp="3kqaem29u9p71">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1237" data-mp="3fqxrdgf22g3x">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-1238" data-mp="3ug25alethy68" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-2" data-id="element-1239" data-mp="3pijxiycjdozt">Tack!</h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-1240" data-mp="31rocql6hzu22">
            Vi har tagit emot dina kontaktuppgifter för tjänsten som{' '}
            <span className="font-semibold text-[#1A1A1A]" data-id="element-1241" data-mp="2wu4t15r16p2q">{job.title}</span>.
            Vi återkommer till dig så snart vi kan!
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-1242" data-mp="36r0uwsmfccyb">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-1243" data-mp="qytps87kbaeo">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-1244" data-mp="2rqx13nwmn5an">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-1245" data-mp="30ophkve34izm">
          <div className="flex items-start gap-4" data-id="element-1246" data-mp="35qeu6fgyh4pt">
            <div className="text-3xl bg-[#FFF8F0] w-12 h-12 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-1247" data-mp="3ao6ofg52p2x6">
              {job.emoji}
            </div>
            <div data-id="element-1248" data-mp="3kdq309lr6am8">
              <h3 className="text-xl font-serif text-[#1A1A1A]" data-id="element-1249" data-mp="3kd82h0s25a8i">
                Komplettera ansökan
              </h3>
              <p className="text-[#C5A55A] font-medium text-xs uppercase tracking-wide" data-id="element-1250" data-mp="3oea23ipvzob0">
                {job.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-1251" data-mp="27vaw2g99975">
            <X className="w-5 h-5" data-id="element-1252" data-mp="7cfkepxkems8" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6" data-id="element-1253" data-mp="2dcfd5hl4gk9">
          <div className="mb-6 p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 text-sm text-[#1A1A1A]/70 leading-relaxed" data-id="element-1254" data-mp="2e5oue7xhk63">
            <p data-id="element-1255" data-mp="v29xn8lsqdd0">
              <strong data-id="element-1256" data-mp="v32yxu1zkanr">Har du redan sökt tjänsten?</strong> Om du saknade
              möjlighet att lämna dina kontaktuppgifter i din tidigare ansökan,
              fyll i dem här så att vi kan återkoppla till dig.
            </p>
          </div>

          <form id="supplement-form" onSubmit={handleSubmit} className="space-y-5" data-id="element-1257" data-mp="lax7qmjk5x9m">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1258" data-mp="1dt15g5q4xvpa">
              <div data-id="element-1259" data-mp="1duc9m6nht5gx">
                <label className={labelClass} data-id="element-1260" data-mp="192ccelyt4qo1">Namn {required}</label>
                <div className="relative" data-id="element-1261" data-mp="191188l1g9gwe">
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="För- och efternamn" data-id="element-1262" data-mp="33ag7xhs9qzv1" />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-1263" data-mp="2yieo9asru6uz" />
                </div>
              </div>
              <div data-id="element-1264" data-mp="2pii4nnl8ny6s">
                <label className={labelClass} data-id="element-1265" data-mp="2pj1juwji88h4">Ålder {required}</label>
                <input type="number" name="age" required min="16" max="99" value={formData.age} onChange={handleChange} className={inputClass} placeholder="Din ålder" data-id="element-1266" data-mp="2aliq1fryz52t" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1267" data-mp="dquy8b6lm7be">
              <div data-id="element-1268" data-mp="1ybszpsgfp2mn">
                <label className={labelClass} data-id="element-1269" data-mp="1y96va1oub38n">E-post {required}</label>
                <div className="relative" data-id="element-1270" data-mp="1tdhobdahqk2b">
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="din@epost.se" data-id="element-1271" data-mp="2812s79f6wnba" />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-1272" data-mp="2d0xjhe2vnewv" />
                </div>
              </div>
              <div data-id="element-1273" data-mp="2qxks4e46ztim">
                <label className={labelClass} data-id="element-1274" data-mp="2m13w6xqr4auz">Telefonnummer {required}</label>
                <div className="relative" data-id="element-1275" data-mp="2lz17ch1c4nru">
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="070-123 45 67" data-id="element-1276" data-mp="wmz0yazzld9x" />
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-1277" data-mp="aqxebzyk4ag8" />
                </div>
              </div>
            </div>

            <div data-id="element-1278" data-mp="kh8k7s1swtpr">
              <label className={labelClass} data-id="element-1279" data-mp="khs0737w5815">
                Hur mycket vill du jobba? {required}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1280" data-mp="3j2juatzbzvjb">
                {['Heltid', 'Halvtid', 'Deltid', 'Extra / vid behov'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-1281" data-mp="3sx21wa7vcb8e">
                      <input type="radio" name="workAmount" value={option} required checked={formData.workAmount === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1282" data-mp="1d5e6ij8i6qrp" />
                      <span className="text-sm text-[#1A1A1A]" data-id="element-1283" data-mp="2iyj1diwp8b0a">{option}</span>
                    </label>)}
              </div>
            </div>

            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1284" data-mp="250kpql9risoh">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1285" data-mp="2020zi8o4yuqb" />
                <p data-id="element-1286" data-mp="203ub82dlw556">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-1287" data-mp="3ufs9j9adngma">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-1288" data-mp="c8bzvji761fm">
            Avbryt
          </Button>
          <Button type="submit" form="supplement-form" isLoading={isSubmitting} className="min-w-[180px]" data-id="element-1289" data-mp="l9s1b1r1at7p">
            <Send className="w-4 h-4 mr-2" data-id="element-1290" data-mp="3pnqhoff67xck" />
            Skicka komplettering
          </Button>
        </div>
      </div>
    </div>;
}